import React, { useRef, useState } from 'react';
import { Alert, Box, Chip, Collapse, IconButton, Link, Tooltip, Typography, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import round from '../../../helpers/round';
import ClayTpmGraphSvg from './ClayTpmGraphSvg';
import ClayKennyGraphSvg from './ClayKennyGraphSvg';
import ClayGibsonGraphSvg from './ClayGibsonGraphSvg';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';

const resultAlertSx = {
  width: '100%',
  minWidth: 0,
  backgroundColor: 'rgba(46,125,50,0.04)',
  alignItems: 'flex-start',
  '& .MuiAlert-icon': {
    alignSelf: 'flex-start',
    mt: 0.25,
    minWidth: 24,
  },
  '& .MuiAlert-action': {
    alignSelf: 'flex-start',
    mt: 0.25,
    width: 32,
    justifyContent: 'center',
    p: 0,
    ml: 1,
  },
  '& .MuiAlert-message': { width: '100%' },
};

const infoAlertSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiAlert-message': { width: '100%', minWidth: 0 },
};

const paidDetailTextSx = {
  mt: 1,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const validityBadge = (isInRange, rangeLabel) => (
  <Chip
    size="small"
    label={
      <span>
        {isInRange ? 'In-range' : 'Extrapolated'} ({rangeLabel})
      </span>
    }
    color={isInRange ? 'success' : 'warning'}
    variant={isInRange ? 'filled' : 'outlined'}
    sx={{ fontWeight: 600 }}
  />
);

const reliabilityBadge = (isInRange, rangeLabel) => {
  const reliability = isInRange ? { label: 'High', color: 'success' } : { label: 'Low', color: 'error' };
  return (
    <Tooltip
      title={`Reliability is High when PI is within the valid method range (${rangeLabel}), and Low when PI is extrapolated outside that range.`}
      arrow
    >
      <Chip
        size="small"
        label={`Reliability: ${reliability.label}`}
        color={reliability.color}
        variant="outlined"
        sx={{ fontWeight: 600 }}
      />
    </Tooltip>
  );
};

const lockActionSx = {
  fontSize: 20,
  color: 'text.disabled',
  cursor: 'not-allowed',
  mr: 1,
  mt: 0.5,
};

const renderDetailsAction = (canUserSeeCalculationSteps, isOpen, setIsOpen) =>
  canUserSeeCalculationSteps ? (
    <IconButton size="small" color="inherit" onClick={() => setIsOpen((v) => !v)} sx={{ width: 32, height: 32 }}>
      <ExpandMoreIcon
        fontSize="small"
        sx={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }}
      />
    </IconButton>
  ) : (
    <Tooltip
      title={
        <span>
          Upgrade to unlock calculation details.{' '}
          <Link href="/pricing" target="_blank" rel="noopener" style={{ color: '#90caf9', textDecoration: 'underline' }}>
            See plans
          </Link>
        </span>
      }
      leaveDelay={500}
      arrow
    >
      <LockOutlinedIcon sx={lockActionSx} />
    </Tooltip>
  );

const ClayEffectiveFrictionAngleResult = ({ result }) => {
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const piValue = Number(result.inputs.pi);
  const tpmGraphRef = useRef(null);
  const kennyGraphRef = useRef(null);
  const gibsonGraphRef = useRef(null);

  const { outputs } = result;
  const phiTPM = Number(outputs?.phi_TPM);
  const sinPhiKenny = Number(outputs?.sinPhi_Kenny);
  const phiKenny = Number(outputs?.phi_Kenny);
  const phiGibson = Number(outputs?.phi_Gibson);
  const phiResGibson = Number(outputs?.phiRes_Gibson);
  const canUserSeeCalculationSteps = Boolean(outputs?.canUserSeeCalculationSteps);

  const isTpmPiOk = piValue >= 5 && piValue <= 1000;
  const isKennyPiOk = piValue >= 7 && piValue <= 115;
  const isGibsonPiOk = piValue >= 13 && piValue <= 117;

  const handleDownloadGraph = async (graphRef, fileName) => {
    try {
      const pngBase64 = await svgContainerToPngBase64(graphRef.current, {
        scale: 4,
        inlineImages: true,
      });
      if (!pngBase64) return;

      const a = document.createElement('a');
      a.href = `data:image/png;base64,${pngBase64}`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
    }
  };

  const renderDownloadAction = (graphRef, fileName) => (
    <Tooltip title="Download graph as PNG" arrow>
      <IconButton size="small" color="primary" onClick={() => handleDownloadGraph(graphRef, fileName)}>
        <DownloadOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const TpmResult = () => {
    const [showDetails, setShowDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify', width: '100%', minWidth: 0 }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />} sx={infoAlertSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ fontSize: '1.05rem' }}>
              Terzaghi, Peck, Mesri (1996): PI vs &phi;&apos;
            </Typography>
            {renderDownloadAction(tpmGraphRef, 'clay-effective-friction-angle-tpm-graph.png')}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, textAlign: 'justify', mb: 2 }}>
            This correlation estimates effective friction angle (&phi;&apos;) from Plasticity Index (PI). The graph below is
            used to select &phi;&apos; for the input PI.
          </Typography>
          <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'center' }} ref={tpmGraphRef}>
            <Box sx={{ width: '100%', maxWidth: 480 }}>
              <ClayTpmGraphSvg pi={piValue} phi={phiTPM} showOverlay={isTpmPiOk} />
            </Box>
          </Box>
          {!isTpmPiOk && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Plasticity Index (PI) provided ({round(piValue, 2)}%) is out of the limits of the graph. Results may not be
                accurate. For Terzaghi, Peck, Mesri (1996), valid PI values range between 5% - 1000%.
              </Typography>
            </Alert>
          )}
          <Alert icon={isSmallScreen ? false : <CheckCircleSuccessIcon />} severity="success" variant="outlined" sx={resultAlertSx} action={renderDetailsAction(canUserSeeCalculationSteps, showDetails, setShowDetails)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: isSmallScreen ? '1 1 100%' : '1 1 260px' }}>
                <b>&phi;&apos;</b> (effective friction angle): {round(phiTPM, 2)}
                <sup>o</sup>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: isSmallScreen ? 'flex-start' : 'flex-end',
                  flex: isSmallScreen ? '1 1 100%' : '0 0 auto',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                {validityBadge(
                  isTpmPiOk,
                  <>
                    5 &le; PI &le; 1000
                  </>
                )}
                {reliabilityBadge(isTpmPiOk, '5 ≤ PI ≤ 1000')}
              </Box>
            </Box>
            {canUserSeeCalculationSteps && (
              <Collapse in={showDetails} unmountOnExit sx={{ width: '100%', minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={paidDetailTextSx}>
                  Literature context: Terzaghi, Peck, and Mesri (1996) compiled drained shear-strength behavior showing that
                  effective friction angle generally decreases as PI increases for clay-rich soils. Their chart represents an
                  empirical trend with visible scatter, because mineralogy, clay fraction, and effective stress level influence
                  measured &phi;&apos; values. This method is best used for preliminary parameter selection, then refined with
                  project-specific effective-stress laboratory testing for critical design.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={paidDetailTextSx}>
                  For PI = {round(piValue, 2)}% &rarr; &phi;&apos; = {round(phiTPM, 2)}
                  <sup>o</sup>
                </Typography>
              </Collapse>
            )}
          </Alert>
        </Alert>
      </Box>
    );
  };

  const KennyResult = () => {
    const [showDetails, setShowDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify', width: '100%', minWidth: 0 }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />} sx={infoAlertSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ fontSize: '1.05rem' }}>
              Kenny (1959): PI vs sin(&phi;&apos;)
            </Typography>
            {renderDownloadAction(kennyGraphRef, 'clay-effective-friction-angle-kenny-graph.png')}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, textAlign: 'justify', mb: 2 }}>
            Kenny&apos;s chart estimates sin(&phi;&apos;) from PI, then &phi;&apos; is obtained by inverse sine.
          </Typography>
          <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'center' }} ref={kennyGraphRef}>
            <Box sx={{ width: '100%', maxWidth: 480 }}>
              <ClayKennyGraphSvg pi={piValue} sinPhi={sinPhiKenny} showOverlay={isKennyPiOk} />
            </Box>
          </Box>
          {!isKennyPiOk && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Plasticity Index (PI) provided ({round(piValue, 2)}%) is out of the limits of the graph. Results may not be
                accurate. For Kenny (1959), valid PI values range between 7% - 115%.
              </Typography>
            </Alert>
          )}
          <Alert icon={isSmallScreen ? false : <CheckCircleSuccessIcon />} severity="success" variant="outlined" sx={resultAlertSx} action={renderDetailsAction(canUserSeeCalculationSteps, showDetails, setShowDetails)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: isSmallScreen ? '1 1 100%' : '1 1 260px' }}>
                <b>sin(&phi;&apos;)</b>: {round(sinPhiKenny, 3)} -> <b>&phi;&apos;</b>: {round(phiKenny, 2)}
                <sup>o</sup>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: isSmallScreen ? 'flex-start' : 'flex-end',
                  flex: isSmallScreen ? '1 1 100%' : '0 0 auto',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                {validityBadge(
                  isKennyPiOk,
                  <>
                    7 &le; PI &le; 115
                  </>
                )}
                {reliabilityBadge(isKennyPiOk, '7 ≤ PI ≤ 115')}
              </Box>
            </Box>
            {canUserSeeCalculationSteps && (
              <Collapse in={showDetails} unmountOnExit sx={{ width: '100%', minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={paidDetailTextSx}>
                  Literature context: Kenney (1959) presented an empirical correlation between PI and sin(&phi;&apos;) for
                  normally consolidated clays. The reported trend indicates that increasing plasticity corresponds to lower
                  drained friction response. The dataset also shows scatter, which reflects differences in clay activity,
                  structure, and composition across soils. Use this relation as a screening-level estimate and calibrate final
                  values with effective-stress triaxial or direct shear testing when needed.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={paidDetailTextSx}>
                  For PI = {round(piValue, 2)}% &rarr; sin(&phi;&apos;) = {round(sinPhiKenny, 3)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={paidDetailTextSx}>
                  &phi;&apos; = asin({round(sinPhiKenny, 3)}) &rarr; {round(phiKenny, 2)}
                  <sup>o</sup>
                </Typography>
              </Collapse>
            )}
          </Alert>
        </Alert>
      </Box>
    );
  };

  const GibsonResult = () => {
    const [showDetails, setShowDetails] = useState(false);

    return (
      <Box sx={{ my: 2, textAlign: 'justify', width: '100%', minWidth: 0 }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />} sx={infoAlertSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ fontSize: '1.05rem' }}>
              Gibson (1953): PI vs &phi;&apos; and &phi;&apos;<sub>r</sub>
            </Typography>
            {renderDownloadAction(gibsonGraphRef, 'clay-effective-friction-angle-gibson-graph.png')}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, textAlign: 'justify', mb: 2 }}>
            Gibson&apos;s correlations provide both effective friction angle (&phi;&apos;) and residual effective friction angle
            (&phi;&apos;<sub>r</sub>) from PI.
          </Typography>
          <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'center' }} ref={gibsonGraphRef}>
            <Box sx={{ width: '100%', maxWidth: 480 }}>
              <ClayGibsonGraphSvg pi={piValue} phi={phiGibson} phiRes={phiResGibson} showOverlay={isGibsonPiOk} />
            </Box>
          </Box>
          {!isGibsonPiOk && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Plasticity Index (PI) provided ({round(piValue, 2)}%) is out of the limits of the graph. Results may not be
                accurate. For Gibson (1953), valid PI values range between 13% - 117%.
              </Typography>
            </Alert>
          )}
          <Alert icon={isSmallScreen ? false : <CheckCircleSuccessIcon />} severity="success" variant="outlined" sx={resultAlertSx} action={renderDetailsAction(canUserSeeCalculationSteps, showDetails, setShowDetails)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: isSmallScreen ? '1 1 100%' : '1 1 260px' }}>
                <b>&phi;&apos;</b>: {round(phiGibson, 2)}
                <sup>o</sup>{' '}
                | <b>&phi;&apos;<sub>r</sub></b>: {round(phiResGibson, 2)}
                <sup>o</sup>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: isSmallScreen ? 'flex-start' : 'flex-end',
                  flex: isSmallScreen ? '1 1 100%' : '0 0 auto',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                {validityBadge(
                  isGibsonPiOk,
                  <>
                    13 &le; PI &le; 117
                  </>
                )}
                {reliabilityBadge(isGibsonPiOk, '13 ≤ PI ≤ 117')}
              </Box>
            </Box>
            {canUserSeeCalculationSteps && (
              <Collapse in={showDetails} unmountOnExit sx={{ width: '100%', minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={paidDetailTextSx}>
                  Literature context: Gibson (1953) reported PI-based trends for both effective friction angle (&phi;&apos;) and
                  residual friction angle (&phi;&apos;<sub>r</sub>) in clays. Both parameters generally reduce with increasing PI,
                  and residual values are lower than peak/true drained values. This distinction is useful in practice because
                  first-time loading often relates to peak strength, while re-sheared or long-term slip conditions may be
                  governed by residual strength. For slope or reactivation problems, residual assumptions should be checked with
                  laboratory and site evidence.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={paidDetailTextSx}>
                  For PI = {round(piValue, 2)}% &rarr; &phi;&apos; = {round(phiGibson, 2)}
                  <sup>o</sup>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={paidDetailTextSx}>
                  For PI = {round(piValue, 2)}% &rarr; &phi;&apos;<sub>r</sub> = {round(phiResGibson, 2)}
                  <sup>o</sup>
                </Typography>
              </Collapse>
            )}
          </Alert>
        </Alert>
      </Box>
    );
  };

  return (
    <section className="output clayEffectiveOutput" style={{ width: '100%', minWidth: 0 }}>
      <h2>Output </h2>
      <TpmResult />
      <KennyResult />
      <GibsonResult />
    </section>
  );
};

export default React.memo(ClayEffectiveFrictionAngleResult);
